import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RetellClient } from "retell-client-js-sdk";
import { initNotificationSocket } from '../../utils/initNotificationSocket';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import { createPythonCallMonitor, isPythonLiveCallId } from '../../utils/apiBackend';
import styles from './LiveMonitoringDashboard.module.css';

// Seconds format helper (e.g. 0:28)
const formatTime = (seconds) => {
  if (!seconds && seconds !== 0) return '';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

// safeParseJson / formatJson / getToolStatus hata diye gaye — wo sirf tool call
// aur tool result cards ke liye the, jo ab feed me dikhte hi nahi.

const LiveMonitoringDashboard = () => {
  const { callId } = useParams();
  const navigate = useNavigate();
  // Python agent ki call ("pc_..."): Retell ka monitorCall nahi chalta. Sunna
  // aur Take Over Python se hote hain (createPythonCallMonitor).
  const isPythonCall = isPythonLiveCallId(callId);
  const [messages, setMessages] = useState(new Map());
  const [callStatus, setCallStatus] = useState('live');
  const [isHangingUp, setIsHangingUp] = useState(false);
  const [showHangupConfirm, setShowHangupConfirm] = useState(false);
  const [fromNumber, setFromNumber] = useState('');
  const bottomRef = useRef(null);

  const retellClientRef = useRef(null);
  const watchRef = useRef(null);
  const isTakenOverRef = useRef(false);
  const callStatusRef = useRef('live');
  const [isListening, setIsListening] = useState(false);
  const [isTakenOver, setIsTakenOver] = useState(false);
  const [monitorStatus, setMonitorStatus] = useState("idle");
  const [showTakeoverConfirm, setShowTakeoverConfirm] = useState(false);
  const [showBackConfirm, setShowBackConfirm] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchInitialHistory = async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:2512/api";
        const token = localStorage.getItem("token");
        const res = await fetch(`${baseUrl}/agent/live-monitoring/${callId}/transcript`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const data = await res.json();
        if (!isMounted) return;

        if (data?.success && Array.isArray(data.transcripts)) {
          setMessages((prev) => {
            const map = new Map();
            // Add history first
            data.transcripts.forEach((item) => {
              if (item && item.id) map.set(item.id, item);
            });
            // Keep any live updates that arrived while fetch was in-flight
            prev.forEach((item, id) => map.set(id, item));
            return map;
          });

          if (data.callStatus === "ended" || data.isLive === false) {
            callStatusRef.current = 'ended';
            setCallStatus("ended");
          }
          if (data?.from_number) {
            setFromNumber(data.from_number);
          }
        }
      } catch (err) {
        console.error("[LiveMonitoring] Failed to fetch initial transcript:", err);
      }
    };

    if (callId) {
      fetchInitialHistory();
    }

    return () => {
      isMounted = false;
    };
  }, [callId]);

  useEffect(() => {
    if (callId) {
      startMonitorSession();
    }
    return () => {
      try {
        if (callStatusRef.current === 'live') {
          watchRef.current?.stopListening();
        }
      } catch (err) {
        // ignore
      }
    };
  }, [callId]);

  useEffect(() => {
    const socket = initNotificationSocket();
    if (!socket) return;

    const onSnapshot = (payload) => {
      if (payload.callId !== callId) return;
      setMessages((prev) => {
        const map = new Map(prev);
        (payload.transcripts || []).forEach((item) => {
          if (item && item.id) map.set(item.id, item);
        });
        return map;
      });
    };

    const onUpdated = (payload) => {
      if (payload.callId !== callId) return;
      setMessages((prev) => {
        const map = new Map(prev);
        (payload.transcripts || []).forEach((item) => {
          if (item && item.id) map.set(item.id, item);
        });
        return map;
      });
    };


    const onEnded = (payload) => {
      console.log('[onEnded] reason:', payload.reason, '| callId:', payload.callId);
      if (payload.callId !== callId) return;
      if (payload.reason === 'call_take_over') return;
      isTakenOverRef.current = false;
      setIsTakenOver(false);
      setIsListening(false);
      callStatusRef.current = 'ended';
      setCallStatus('ended');
      setIsHangingUp(false);
    };
    socket.on('live_transcript_snapshot', onSnapshot);
    socket.on('live_transcript_updated', onUpdated);
    socket.on('live_call_ended', onEnded);

    return () => {
      socket.off('live_transcript_snapshot', onSnapshot);
      socket.off('live_transcript_updated', onUpdated);
      socket.off('live_call_ended', onEnded);
    };
  }, [callId]);

  // Hang Up handler
  const handleHangup = async () => {
    if (callStatus !== 'live' || isHangingUp) return;
    setShowHangupConfirm(false);
    setIsHangingUp(true);


    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:2512/api";
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/agent/live-monitoring/${callId}/hangup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await res.json();

      if (!data?.success) {
        console.error("[LiveMonitoring] Hangup failed:", data?.message);
        setIsHangingUp(false);
      }
      // On success, isHangingUp stays true until 'live_call_ended' socket event
      // arrives and flips callStatus to 'ended' (handled in onEnded above)
    } catch (err) {
      console.error("[LiveMonitoring] Hangup request error:", err);
      setIsHangingUp(false);
    }
  };

  const startMonitorSession = async () => {
    if (isPythonCall) {
      let started = false;
      watchRef.current = createPythonCallMonitor(callId, {
        onStatus: (status) => {
          if (status === "listening") {
            started = true;
            setIsListening(true);
          } else if (status === "taken_over") {
            // Retell jaisa: ab sunna alag se nahi, call hi aapke paas hai.
            setIsListening(false);
            setIsTakenOver(true);
            isTakenOverRef.current = true;
          } else if (status === "ended") {
            setIsListening(false);
            setIsTakenOver(false);
            isTakenOverRef.current = false;
            callStatusRef.current = 'ended';
            setCallStatus('ended');
            setIsHangingUp(false);
          } else if (status === "stopped") {
            setIsListening(false);
            if (!started && callStatusRef.current === 'live') {
              alert("Could not start live listening for this call. Please try again.");
            }
            started = false;
          }
        },
        onError: (err) => console.error("Live listen error:", err),
      });
      return;
    }
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:2512/api";
      const token = localStorage.getItem('token');
      const res = await fetch(`${baseUrl}/agent/retell-config`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (!data.success) throw new Error("Public key fetch failed");

      const client = new RetellClient({ key: data.publicKey });
      retellClientRef.current = client;

      const watch = client.monitorCall({
        call_id: callId,
        transcript: false,
        hooks: {
          onStatus: (status) => {
            console.log("Monitor status:", status);
            setMonitorStatus(status);
            if (status === "listening" && !isTakenOverRef.current) setIsListening(true);
            if (status === "taken_over") {
              setIsTakenOver(true);
              isTakenOverRef.current = true;
            }
            if (status === "ended") {
              setIsListening(false);
              setIsTakenOver(false);
              isTakenOverRef.current = false;
              callStatusRef.current = 'ended';
              setCallStatus('ended');
              setIsHangingUp(false);
            }
          },
          onError: (err) => console.error("Monitor error:", err),
        },
      });

      watchRef.current = watch;
    } catch (err) {
      console.error("Monitor session start failed:", err);
    }
  };

  const handleLiveListen = () => watchRef.current?.listen();

  const handleStopListening = () => {
    watchRef.current?.stopListening();
    setIsListening(false);
  };

  const handleTakeOver = async () => {
    setShowTakeoverConfirm(true);
  };

  const confirmTakeOver = async () => {
    setShowTakeoverConfirm(false);
    try {
      isTakenOverRef.current = true;
      await watchRef.current?.takeOver();
    } catch (err) {
      isTakenOverRef.current = false;
      alert("Microphone permission denied or the call has already been taken over.");
    }
  };
  const handleTakeoverEndCall = () => {
    console.log('[TakeoverEnd] called, isTakenOverRef:', isTakenOverRef.current);
    isTakenOverRef.current = false;
    setIsTakenOver(false);
    watchRef.current?.end();
    callStatusRef.current = 'ended';
    setCallStatus('ended');
    setIsHangingUp(false);

  };


  // Auto scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /*
   * Feed me sirf baat-cheet dikhti hai. Tool calls aur unke results (jaise
   * book_calendar_event aur uska HTTP 400 wala jawab, ya transfer_* calls)
   * internal plumbing hain — user ke liye shor hain, aur error responses raw
   * API detail bhi leak karte hain (HTTP 400, axios error codes waghairah).
   * Socket se aate rahenge aur `messages` me store bhi honge, bas feed me
   * render nahi honge.
   */
  // node_transition: conversation-flow agent ke node badalna (begin → router) — internal, feed me nahi
  const HIDDEN_ROLES = ['tool_call_invocation', 'tool_call_result', 'node_transition'];

  const sortedMessages = [...messages.values()]
    .filter((item) => !HIDDEN_ROLES.includes(item.role))
    .sort((a, b) => a.time_sec - b.time_sec);

  const lastMessageTime = sortedMessages.length > 0
    ? sortedMessages[sortedMessages.length - 1].time_sec
    : 0;

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button
              type="button"
              className={styles.backBtn}
              onClick={() => {
                if (isTakenOver) {
                  setShowBackConfirm(true);
                } else {
                  const from = sessionStorage.getItem('liveMonitoringFrom');
                  const activeCallsCount = parseInt(sessionStorage.getItem('activeCallsCount') || '0');
                  if (from === 'active-calls' && activeCallsCount > 1) {
                    navigate('/live-monitoring/active-calls');
                  } else {
                    sessionStorage.removeItem('liveMonitoringFrom');
                    sessionStorage.removeItem('activeCallsCount');
                    navigate('/fast-agent-detail');
                  }
                }
              }}
              aria-label="Back"
            >
              <img src="/svg/Back-icon.svg" alt="" />
            </button>
            <div className={styles.titleGroup}>
              <h1 className={styles.title}>Live Monitoring</h1>
              {/* <p className={styles.subtitle}>Agent: Sophia · DesignersX</p> */}
            </div>
          </div>

          <div className={styles.headerRight}>
            <div className={`${styles.statusPill} ${callStatus === 'live' ? styles.live : styles.ended}`}>
              <span className={styles.statusDot}></span>
              {callStatus === 'live' ? 'Live' : 'Ended'}
            </div>
          </div>
        </div>

        {/* Call ID Bar */}
        <div className={styles.callIdBar}>
          <div className={styles.callIdLeft}>
            <svg className={styles.phoneIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
            </svg>
            <span className={styles.callIdText}>{callId}</span>
          </div>
          {fromNumber && (
            <span className={styles.fromNumberText}>{fromNumber}</span>
          )}
        </div>

        {/* Message Feed */}
        <div className={styles.chatArea}>
          {sortedMessages.length === 0 && (
            <div className={styles.emptyState}>
              {callStatus === 'ended' ? 'No transcript available for this call.' : 'Waiting for transcript...'}
            </div>
          )}

          {sortedMessages.map((item) => {
            // User Message
            if (item.role === 'user') {
              return (
                <div key={item.id} className={`${styles.msgRow} ${styles.user}`}>
                  <div className={styles.senderLabel}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                    Customer
                  </div>
                  <div className={`${styles.bubble} ${styles.user}`}>
                    {item.content}
                  </div>
                  {item.time_sec !== undefined && (
                    <span className={`${styles.timestamp} ${styles.user}`}>{formatTime(item.time_sec)}</span>
                  )}
                </div>
              );
            }

            // Agent Message
            if (item.role === 'agent') {
              return (
                <div key={item.id} className={`${styles.msgRow} ${styles.agent}`}>
                  <div className={styles.senderLabel}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="10" rx="2"></rect>
                      <circle cx="12" cy="5" r="2"></circle>
                      <path d="M12 7v4"></path>
                    </svg>
                    Agent
                  </div>
                  <div className={`${styles.bubble} ${styles.agent}`}>
                    {item.content}
                  </div>
                  {item.time_sec !== undefined && (
                    <span className={`${styles.timestamp} ${styles.agent}`}>{formatTime(item.time_sec)}</span>
                  )}
                </div>
              );
            }

            // tool calls / node_transition yahan tak pahunchte hi nahi — HIDDEN_ROLES me filter ho chuke.
            return null;
          })}

          <div ref={bottomRef} />
        </div>

        <div className={`${styles.liveActions} live-actions`}>
          {callStatus === "live" && (
            <>
              {!isTakenOver && (
                <>
                  {!isListening ? (
                    <button type="button" className={styles.actionBtn} onClick={handleLiveListen}>
                      🎧 Live Listen
                    </button>
                  ) : (
                    <>
                      <span className={styles.listeningBadge}>🟢 Listening...</span>
                      <button type="button" className={`${styles.actionBtn} ${styles.stopBtn}`} onClick={handleStopListening}>
                        ⏹ Stop listening
                      </button>
                    </>
                  )}
                  <button type="button" className={`${styles.actionBtn} ${styles.takeOverBtn}`} onClick={handleTakeOver}>
                    🎤 Take Over
                  </button>
                </>
              )}

              {isTakenOver && (
                <div className={`${styles.takeoverActive} takeover-active`}>
                  <div className={styles.takeoverBanner}>
                    <span className={styles.takeoverIcon}>🎤</span>
                    <div className={styles.takeoverText}>
                      <p className={styles.takeoverTitle}>You have taken over the call</p>
                      <p className={styles.takeoverSub}>AI agent has been permanently stopped</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`${styles.actionBtn} ${styles.endCallBtn}`}
                    onClick={handleTakeoverEndCall}    >
                    End Call
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          {callStatus === 'live' ? (
            <div className={styles.footerContent}>
              <span className={styles.footerLiveDot}></span>
              <span>Call in progress · {formatTime(lastMessageTime)}</span>
            </div>
          ) : (
            <div className={styles.footerContent}>
              <span className={styles.footerEndedDot}></span>
              <span>Call has ended</span>
            </div>
          )}

          {callStatus === 'live' && !isTakenOver && (
            <button
              type="button"
              className={styles.hangupBtn}
              onClick={() => setShowHangupConfirm(true)}
              disabled={isHangingUp}
            >
              {isHangingUp ? 'Ending…' : 'Hang Up'}
            </button>
          )}
        </div>

      </div>

      <ConfirmModal
        show={showHangupConfirm}
        type="failed"
        title="End this call?"
        message="Are you sure you want to hang up? Rexpt is not responsible for this. This can hamper the user experience."
        confirmText="Hang Up"
        cancelText="Go Back"
        showCancel
        isLoading={isHangingUp}
        onConfirm={handleHangup}
        onClose={() => setShowHangupConfirm(false)}
      />
      <ConfirmModal
        show={showTakeoverConfirm}
        type="failed"
        title="Take Over this call?"
        message="This will permanently stop the AI agent. Are you sure you want to take over?"
        confirmText="Take Over"
        cancelText="Go Back"
        showCancel
        onConfirm={confirmTakeOver}
        onClose={() => setShowTakeoverConfirm(false)}
      />
      <ConfirmModal
        show={showBackConfirm}
        title="Call will end"
        message="If you go back, the call will be disconnected. Are you sure you want to go back?"
        type="info"
        confirmText="Yes, Go Back"
        cancelText="Cancel"
        showCancel={true}
        onConfirm={() => {
          watchRef.current?.end();
          navigate('/fast-agent-detail');
        }}
        onClose={() => setShowBackConfirm(false)}
      />
    </div>

  );
};

export default LiveMonitoringDashboard;